import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import classNames from 'classnames';

import { withStyles } from '@material-ui/core/styles';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Typography from '@material-ui/core/Typography';

import IconButton from 'components/common/IconButton';
import Badge from 'components/common/Badge';
import Menu, { MenuItem } from 'components/common/Menu';
import Tooltip from 'components/common/Tooltip';
import DeleteMenu from 'components/common/DeleteMenu';
import IconMoreVert from 'components/icons/IconMoreVert';
import Progress from 'components/common/Progress';
import Link from 'components/common/Link';

import formatPrice from 'utils/formatPrice';
import pxToRem from 'styles/pxToRem';
import pxToEm from 'styles/pxToEm';

import {
  deletePatientInsurance,
  checkPatientInsuranceEligibility,
} from 'redux/modules/patientsInsurance';

import InsuranceCreateEditDialog from 'components/patient/InsuranceCreateEditDialog';

const styles = theme => ({
  tableRow: {
    '&:last-child td': {
      borderBottom: 'none',
    },
  },
  tableCell: {
    paddingLeft: 8,
    paddingRight: 8,
    fontSize: pxToRem(13),
    letterSpacing: pxToEm(0.5, 13),
    [theme.breakpoints.down('sm')]: {
      paddingLeft: 4,
      paddingRight: 4,
    },
    '&:first-child': {
      paddingLeft: 25,
      [theme.breakpoints.down('sm')]: {
        paddingLeft: 8,
      },
    },
  },
  tableCellInsuranceName: {
    minWidth: 150,
  },
  tableCellNoWrap: {
    whiteSpace: 'nowrap',
  },
  tableCellButton: {
    padding: '0 12px 0 10px',
    width: 24,
    [theme.breakpoints.down('sm')]: {
      padding: 0,
    },
  },
  tableCellOrderBenefits: {
    width: 18,
  },
  tableCellInsuranceStatusInactive: {
    color: theme.palette.validation[500],
  },
  tableCellInsuranceID: {
    wordBreak: 'break-all',
    minWidth: 94,
    [theme.breakpoints.down('sm')]: {
      minWidth: 50,
    },
  },
  tableCellCopay: {
    wordBreak: 'break-all',
    minWidth: 52,
  },
  caption: {
    display: 'inline-block',
    verticalAlign: 'middle',
    fontSize: pxToRem(13),
  },
  iconMoreVert: {
    width: 24,
    height: 24,
  },
  iconCheck: {
    display: 'inline-block',
    width: 18,
    height: 14,
    marginRight: 10,
    verticalAlign: 'middle',
    color: theme.palette.primary[500],
  },
  iconCard: {
    width: 32,
    height: 18,
    marginRight: 10,
    verticalAlign: 'middle',
  },
  iconNoZirmedInfo: {
    display: 'inline-block',
    width: 8,
    height: 8,
    margin: '-1px 8px 0 0',
    borderRadius: '50%',
    cursor: 'pointer',
    verticalAlign: 'middle',
    backgroundColor: theme.palette.validation['500'],
  },
  typographyService: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    borderRadius: '50%',
    color: theme.palette.common.white,
    cursor: 'pointer',
    backgroundColor: theme.palette.primary[500],
  },
  desktopOnly: {
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
});

class InsuranceInformationItem extends Component {
  constructor(props) {
    super(props);

    this.state = {
      anchorEl: null,
      editDialogOpen: false,
      deleteMenuOpen: false,
    };

    this.onMenuButtonClick = this.onMenuButtonClick.bind(this);
    this.onMenuClose = this.onMenuClose.bind(this);
    this.onEditMenuItemClick = this.onEditMenuItemClick.bind(this);
    this.onEditDialogClose = this.onEditDialogClose.bind(this);
    this.onDeleteMenuItemClick = this.onDeleteMenuItemClick.bind(this);
    this.onDeleteCancel = this.onDeleteCancel.bind(this);
    this.onDeleteSubmit = this.onDeleteSubmit.bind(this);
    this.onCheckEligibilityMenuItemClick = this.onCheckEligibilityMenuItemClick.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    if (this.props['Checking Eligibility']
      && !nextProps['Checking Eligibility']
      && this.state.anchorEl) {
      this.setState({ anchorEl: null });
    }
  }

  onMenuButtonClick(event) {
    this.setState({
      anchorEl: event.currentTarget,
      deleteMenuOpen: false,
    });
  }

  onMenuClose() {
    this.setState({ anchorEl: null });
  }

  onEditMenuItemClick() {
    this.onMenuClose();
    this.setState({ editDialogOpen: true });
  }

  onEditDialogClose() {
    this.setState({ editDialogOpen: false });
  }

  onDeleteMenuItemClick() {
    this.setState({ deleteMenuOpen: true });
  }

  onDeleteCancel() {
    this.setState({ anchorEl: null });
  }

  onDeleteSubmit() {
    this.props.actions.deletePatientInsurance({
      'Record Number': this.props['User Record Number'],
      ID: this.props.ID,
    });
    this.setState({
      deleteMenuOpen: false,
      anchorEl: null,
    });
  }

  onCheckEligibilityMenuItemClick() {
    this.props.actions.checkPatientInsuranceEligibility({
      'Patient ID': this.props['User Record Number'],
      'Insurance ID': this.props.ID,
      'Payer Name': this.props['Payer Name'],
    });
  }

  render() {
    const { account, appInfo, classes } = this.props;
    const open = Boolean(this.state.anchorEl);
    const phoneContacts = this.props['Insurance Info'] && this.props['Insurance Info'].Contacts
      ? this.props['Insurance Info'].Contacts
      .filter(item => item.type === 'Phone')
      .map(item => ({
        Value: item.value,
        Description: item.description,
      })) : [];
    const faxContacts = this.props['Insurance Info'] && this.props['Insurance Info'].Contacts
      ? this.props['Insurance Info'].Contacts
      .filter(item => item.type === 'Fax')
      .map(item => ({
          Value: item.value,
          Description: item.description,
        })) : [];
    const emailContacts = this.props['Insurance Info'] && this.props['Insurance Info'].Contacts
      ? this.props['Insurance Info'].Contacts
      .filter(item => item.type === 'Email')
      .map(item => ({
          Value: item.value,
          Description: item.description,
        })) : [];
    return (
      <TableRow className={classes.tableRow}>
        <TableCell
          className={classNames(classes.tableCell, classes.tableCellOrderBenefits)}
        >
          {this.props['Order Benefits'] &&
            <Badge
              color="primary"
              tooltipText={this.props['Order Benefits']}
            >
              {this.props['Order Benefits'] === 'Primary' && '1'}
              {this.props['Order Benefits'] === 'Secondary' && '2'}
              {this.props['Order Benefits'] === 'Secondary As Primary' && '2'}
              {this.props['Order Benefits'] === 'Tertiary' && '3'}
            </Badge>
          }
        </TableCell>
        <TableCell
           className={classNames(classes.tableCell, classes.tableCellInsuranceName)}
        >
          {!this.props['Insurance Info']['Is Active'] &&
            <Tooltip
              content={
                <Typography>
                  Insurance company is Inactive
                </Typography>
              }
              placement="top"
            >
              <div className={classes.iconNoZirmedInfo} />
            </Tooltip>
          }
          <Link
            onClick={this.onEditMenuItemClick}
            size="md"
          >
            {this.props['Insurance Info']['Payer Name']}
          </Link>
        </TableCell>
        <TableCell
          className={classNames(classes.tableCell, classes.desktopOnly)}
        >
          {this.props['Claim Filling Code']['Full Name']}
        </TableCell>
        <TableCell
          className={classNames(classes.tableCell, classes.tableCellInsuranceID)}
        >
          {this.props['Insurance ID']}
        </TableCell>
        <TableCell
          className={classNames(classes.tableCell, classes.desktopOnly)}
        >
          {this.props['Effective From']}
          {(this.props['Effective From'] && this.props['Effective To']) && ' - '}
          {(this.props['Effective To'] && !this.props['Effective From']) && '...-'}
          {this.props['Effective To']}
        </TableCell>
        <TableCell
          className={classNames(classes.tableCell, classes.desktopOnly, {
            [classes.tableCellCopay]: this.props['Copay Type'] === 'Fixed',
          })}
        >
          {this.props['Copay Type'] === 'Fixed' && (
            formatPrice(this.props['Copay Amount'])
          )}
          {this.props['Copay Type'] === 'Percent' && (
            `${this.props['Copay Amount']}%`
          )}
        </TableCell>
        <TableCell
          className={classNames(classes.tableCell, classes.desktopOnly, classes.tableCellNoWrap, {
            [classes.tableCellInsuranceStatusInactive]: this.props['Insurance Status'] === 'Inactive',
          })}
        >
          {this.props['Insurance Status']}
        </TableCell>
        <TableCell
          className={
            classNames(classes.tableCell, classes.tableCellButton)
          }
          padding="none"
          numeric
        >
          {(this.props['Insurance Info']['Claim Md Payer']
            && this.props['Insurance Info']['Claim Md Payer'].Eligibility) &&
            <IconButton
              onClick={this.onCheckEligibilityMenuItemClick}
              disabled={this.props['Checking Eligibility'] || ['demo', 'clients'].indexOf(appInfo['APP NAME']) !== -1}
              tooltipContent={['demo', 'clients'].indexOf(appInfo['APP NAME']) !== -1
                ? 'Paid version needed to Enable Eligibility Check' : 'Check Eligibility'}
            >
              <Typography
                className={classes.typographyService}
                variant="caption"
              >
                E
              </Typography>
              {this.props['Checking Eligibility'] &&
                <Progress size={40} />
              }
            </IconButton>
          }
        </TableCell>
        <TableCell
          className={
            classNames(classes.tableCell, classes.tableCellButton)
          }
          padding="none"
          numeric
        >
          <IconButton
            aria-label="More"
            aria-owns={open ? `${this.props.ID}-insurance-menu` : null}
            aria-haspopup="true"
            onClick={this.onMenuButtonClick}
            tooltipContent="Open Menu"
          >
            <IconMoreVert
              className={classes.iconMoreVert}
            />
          </IconButton>
          <Menu
            id={`${this.props.ID}-insurance-menu`}
            anchorEl={this.state.anchorEl}
            open={open}
            onClose={this.onMenuClose}
            list={!this.state.deleteMenuOpen}
          >
            {this.state.deleteMenuOpen ? (
              <DeleteMenu
                text="Are you sure you want to delete this insurance?"
                onCancel={this.onDeleteCancel}
                onSubmit={this.onDeleteSubmit}
              />
            ) : (
              <Fragment>
                <MenuItem
                  onClick={this.onEditMenuItemClick}
                >
                  Edit
                </MenuItem>
                <MenuItem
                  color="warning"
                  onClick={this.onDeleteMenuItemClick}
                  disabled={account.Roles.filter(item =>
                    item.Name === 'Master Administrator' || item.Name === 'Administrator' || item.Name === 'Manager').length === 0}
                >
                  Delete
                </MenuItem>
              </Fragment>
            )}
          </Menu>
          <InsuranceCreateEditDialog
            open={this.state.editDialogOpen}
            onClose={this.onEditDialogClose}
            onSubmitSuccess={this.onEditDialogClose}
            ID={this.props.ID}
            {...{ 'Checking Eligibility': this.props['Checking Eligibility'] }}
            {...{ 'User Record Number': this.props['User Record Number'] }}
            {...{ 'Is Available Eligibility': (this.props['Insurance Info']['Claim Md Payer']
              && this.props['Insurance Info']['Claim Md Payer'].Eligibility) || false }}
            initialValues={{
              'Payer Name': this.props['Insurance Info']['Payer Name'],
              'Insurance Info ID': this.props['Insurance Info'].ID,
              'Insurance Info Payer Name': this.props['Insurance Info']['Payer Name'],
              'Transmission Method': this.props['Transmission Method'],
              'Insurance ID': this.props['Insurance ID'],
              'Group ID': this.props['Group ID'],
              ...this.props['Claim Filling Code'] && this.props['Claim Filling Code'].ID
                ? {
                  'Claim Filling Code ID': this.props['Claim Filling Code'].ID,
                  'Claim Filling Code Name': this.props['Claim Filling Code']['Full Name'],
                  'Selected Claim Filling Code Name': this.props['Claim Filling Code']['Full Name'],
                } : {},
              ...this.props['Order Benefits']
                ? { 'Order Benefits': this.props['Order Benefits'] }
                : {},
              'Effective From': this.props['Effective From'] || '',
              'Effective To': this.props['Effective To'] || '',
              'Relation to Insured': this.props['Relation to Insured'],
              ...this.props['Copay Type']
                ? { 'Copay Type': this.props['Copay Type'] }
                : { 'Copay Type': 'None' },
              ...this.props['Copay Type'] && this.props['Copay Type'] === 'Fixed'
                ? { 'Copay Amount Fixed': formatPrice(this.props['Copay Amount']) }
                : {},
              ...this.props['Copay Type'] && this.props['Copay Type'] === 'Percent'
                ? { 'Copay Amount Percent': `${this.props['Copay Amount']}%` }
                : {},
              Comments: this.props.Comments || '',
              'Insurance Status': this.props['Insurance Status'] === 'Inactive' ? false : true,
              'Related First Name': this.props['Related First Name'],
              'Related Last Name': this.props['Related Last Name'],
              'Related Date of Birth': this.props['Related Date of Birth'],
              'Related Insurance ID': this.props['Related Insurance ID'],
              'Related Gender': this.props['Related Gender'],
              'Signature On File': this.props['Signature On File'],
              'Auto Accident State': this.props['Auto Accident State'],
              'Insurance Special Date': this.props['Insurance Special Date'],
              'Contacts Phone': phoneContacts.length > 0 ? phoneContacts : [
                {
                  Value: '',
                  Description: '',
                }
              ],
              'Contacts Fax': faxContacts.length > 0 ? faxContacts : [
                {
                  Value: '',
                  Description: '',
                }
              ],
              'Contacts Email': emailContacts.length > 0 ? emailContacts : [
                {
                  Value: '',
                  Description: '',
                }
              ],
            }}
            form={`patientInsuranceInformationEdit-${this.props.ID}`}
          />
        </TableCell>
      </TableRow>
    );
  }
}

InsuranceInformationItem.propTypes = {
  ID: PropTypes.number.isRequired,
  'User Record Number': PropTypes.number.isRequired,
  'Insurance Info': PropTypes.shape({
    ID: PropTypes.number.isRequired,
    'Payer Name': PropTypes.string.isRequired,
    'Is Active': PropTypes.bool.isRequired,
    'Change Info': PropTypes.shape({
      'Is Eligibility': PropTypes.bool,
    }),
  }).isRequired,
  'Insurance ID': PropTypes.string.isRequired,
  'Effective From': PropTypes.string,
  'Effective To': PropTypes.string,
  'Copay Amount': PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  'Insurance Status': PropTypes.string.isRequired,
  'Transmission Method': PropTypes.string.isRequired,
  'Group ID': PropTypes.string,
  'Order Benefits': PropTypes.string,
  'Relation to Insured': PropTypes.string.isRequired,
  'Copay Type': PropTypes.string,
  'Related First Name': PropTypes.string,
  'Related Last Name': PropTypes.string,
  'Related Date of Birth': PropTypes.string,
  'Related Insurance ID': PropTypes.string,
  'Related Gender': PropTypes.string,
  Comments: PropTypes.string,
  'Checking Eligibility': PropTypes.bool,
  'Signature On File': PropTypes.bool,
  'Claim Filling Code': PropTypes.shape({
    ID: PropTypes.number.isRequired,
    'Full Name': PropTypes.string.isRequired,
  }),
  actions: PropTypes.shape({
    deletePatientInsurance: PropTypes.func.isRequired,
    checkPatientInsuranceEligibility: PropTypes.func.isRequired,
  }).isRequired,
  account: PropTypes.shape({
    Roles: PropTypes.arrayOf(PropTypes.shape({
      Name: PropTypes.string.isRequired,
    })).isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    tableRow: PropTypes.string.isRequired,
    tableCell: PropTypes.string.isRequired,
    tableCellButton: PropTypes.string.isRequired,
    tableCellInsuranceStatusInactive: PropTypes.string.isRequired,
    tableCellInsuranceID: PropTypes.string.isRequired,
    tableCellCopay: PropTypes.string.isRequired,
    tableCellNoWrap: PropTypes.string.isRequired,
    caption: PropTypes.string.isRequired,
    iconMoreVert: PropTypes.string.isRequired,
    iconCheck: PropTypes.string.isRequired,
    iconCard: PropTypes.string.isRequired,
    iconNoZirmedInfo: PropTypes.string.isRequired,
  }).isRequired,
};

InsuranceInformationItem.defaultProps = {
  Comments: '',
  'Order Benefits': null,
  'Effective From': null,
  'Effective To': null,
  'Copay Amount': null,
  'Group ID': null,
  'Copay Type': null,
  'Related First Name': null,
  'Related Last Name': null,
  'Related Date of Birth': null,
  'Related Insurance ID': null,
  'Checking Eligibility': false,
  'Signature On File': true,
  'Claim Filling Code': null,
  'Related Gender': null,
};

const mapStateToProps = store => ({
  account: store.account,
  appInfo: store.appInfo,
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    deletePatientInsurance,
    checkPatientInsuranceEligibility,
  }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  withStyles(styles)(InsuranceInformationItem),
);



// WEBPACK FOOTER //
// ./src/components/patient/PatientProfile/InsuranceInformation/InsuranceInformationItem.js